import axios from 'axios';
import { normalize } from 'normalizr';
import { toCollectionPersistenceDto } from '../../mappers/collections/toCollectionPersistenceDto';
import { toCollectionsModel } from '../../mappers/collections/toCollectionsModel';
import { contentActionsService } from '../../utils/ContentActionsService/ContentActionsService';
import { EventSource } from '../../utils/analyticsService';
import { share } from '../../utils/share';
import { PostModel, ReportReasons } from '../posts/types';
import { collectionsSchema } from './schema';
import type {
    CollectionDto,
    CollectionModel,
    CollectionPersistenceModel,
    CollectionsDataModel,
} from './types';

export const DEFAULT_COLLECTION_ID = '-1';

export enum CollectionType {
    SAVED = 'saved',
    CREATED = 'created',
}

interface LoadCollectionParams {
    type?: CollectionType;
    businessId?: string;
    next?: string;
}

export async function loadCollections(params: LoadCollectionParams): Promise<{
    collections: Array<CollectionsDataModel['id']>;
    next?: string;
    records: {
        collections: CollectionsDataModel[];
    };
}> {
    const queryParams = new URLSearchParams();
    params.type && queryParams.set('type', params.type);
    params.businessId && queryParams.set('business_id', params.businessId);
    params.next && queryParams.set('next', params.next);

    const response = await axios.get(`/collections?${queryParams.toString()}`);
    const collections = response.data.collections || [];
    const normalized = normalize(
        {
            collections: collections.map(toCollectionsModel),
        },
        {
            collections: [collectionsSchema],
        },
    );

    return {
        collections: normalized.result.collections,
        next: response.data.next ?? undefined,
        records: {
            collections: Object.values<CollectionsDataModel>(normalized.entities.collections ?? {}),
        },
    };
}

export async function loadCollection(
    collectionId: CollectionsDataModel['id'],
): Promise<CollectionModel> {
    const response = await axios.get(`/collections/${collectionId}`);

    return toCollectionsModel(response.data.collections[0]);
}

interface SaveNewCollectionParams {
    postIds?: PostModel['id'][];
    collectionName: string;
    isPublic: boolean;
    showInFeed: boolean;
    needCpaRegistration?: boolean;
}

export async function saveToNewCollection({
    postIds,
    collectionName,
    isPublic,
    showInFeed,
    needCpaRegistration,
}: SaveNewCollectionParams): Promise<{
    collections: Array<CollectionsDataModel['id']>;
    records: {
        collections: CollectionsDataModel[];
    };
}> {
    const response = await axios('/collections', {
        method: 'POST',
        data: {
            post_ids: postIds,
            display_name: collectionName,
            is_public: isPublic,
            show_in_feed: showInFeed,
            need_cpa_registration: needCpaRegistration,
        },
    });

    const newCollections = response.data.collections.filter(
        (collection: CollectionDto) => collection.id !== DEFAULT_COLLECTION_ID,
    );

    const normalized = normalize(
        {
            collections: newCollections.map(toCollectionsModel),
        },
        {
            collections: [collectionsSchema],
        },
    );

    return {
        collections: normalized.result.collections,
        records: {
            collections: Object.values<CollectionsDataModel>(normalized.entities.collections ?? {}),
        },
    };
}

export async function saveToExistentCollection(
    collectionId: string,
    postId: PostModel['id'],
): Promise<{
    collections: Array<CollectionsDataModel['id']>;
    records: {
        collections: CollectionsDataModel[];
    };
}> {
    const response = await axios('/favourites', {
        method: 'POST',
        data: {
            post_id: postId,
            collection_id: collectionId,
        },
    });

    const collections = response.data.collections || [];
    const normalized = normalize(
        {
            collections: collections.map(toCollectionsModel),
        },
        {
            collections: [collectionsSchema],
        },
    );

    return {
        collections: normalized.result.collections,
        records: {
            collections: Object.values<CollectionsDataModel>(normalized.entities.collections ?? {}),
        },
    };
}

export async function removeCollection(collectionId: CollectionsDataModel['id']): Promise<void> {
    await axios.delete(`/collections/${collectionId}`);
}

export async function saveCollection(collectionId: CollectionsDataModel['id']): Promise<void> {
    await axios.post(`/collections/${collectionId}/save`);
}

export async function removeSavedCollection(
    collectionId: CollectionsDataModel['id'],
): Promise<void> {
    await axios.delete(`/collections/${collectionId}/save`);
}

export async function updateCollection(
    model: CollectionPersistenceModel,
): Promise<CollectionModel> {
    const response = await axios.put(`/collections/${model.id}`, toCollectionPersistenceDto(model));

    return toCollectionsModel(response.data.collections[0]);
}

export async function getCollectionSuggests(id: string, screen: EventSource): Promise<string[]> {
    const response = await axios.get(`/collections/${id}/suggest`);
    contentActionsService.trackCollectionSuggestsGenerate({ id, screen });

    return response?.data?.display_name_suggest;
}

export async function shareCollection(sharingUrl: string): Promise<ReturnType<typeof share>> {
    return await share({ url: sharingUrl });
}

export async function reportCollection(collectionId: string, reason: ReportReasons): Promise<void> {
    await axios.put(
        `/collections/${collectionId}/report`,
        {},
        { params: { report_reason: reason } },
    );
}

export async function updateCollectionReaction(
    model: CollectionModel,
    eventSource: EventSource,
): Promise<Pick<CollectionDto, 'is_liked'>> {
    return await axios(`/collections/${model.id}/reaction`, {
        method: 'POST',
        params: {
            event_source: eventSource,
        },
        data: {
            reaction_state: model.isLiked ? 'none' : 'like',
            log_context: model.logContext,
        },
    });
}
